import { useEffect, useMemo, useState, useRef } from 'react'
import { throttle, last } from 'lodash-es'
import { useField } from 'formik'
import { TargetingCircle } from '../../../__generated__/graphql'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { Request, PlaceType, PlaceDetails, useAutoCompleteInterface } from './intefaces'
import { loadPlacesLibrary } from '../../../utils/googleMapsLoader'
import { normalizeBoundaryName } from '../../../utils/geo/boundaries'

const autocompleteService = { current: null }

type FormattedLocation = {
  name?: string | null
  code?: string | null
  location?: { latitude?: number | null; longitude?: number | null } | null
}

export type UseAutoCompleteCallbacks = {
  /** Called once a picked place has been appended, after Google returned its details */
  onAdded?: (location: FormattedLocation) => void
  /** Called instead of appending when the picked place is already in the list, with its index */
  onDuplicate?: (index: number) => void
}

const SAME_CITY_KM = 0.1
const KM_PER_DEGREE = 111.2

const isSameCity = (a: FormattedLocation, b: FormattedLocation): boolean => {
  const [latA, lngA, latB, lngB] = [
    a.location?.latitude,
    a.location?.longitude,
    b.location?.latitude,
    b.location?.longitude,
  ]
  if ([latA, lngA, latB, lngB].some((value) => typeof value !== 'number')) return false
  const scale = Math.cos(((latA as number) * Math.PI) / 180)
  const dLat = ((latA as number) - (latB as number)) * KM_PER_DEGREE
  const dLng = ((lngA as number) - (lngB as number)) * KM_PER_DEGREE * scale
  return Math.hypot(dLat, dLng) <= SAME_CITY_KM
}

/**
 * Index of the location the picked place duplicates, or -1. A same-named place elsewhere is not a duplicate:
 * cities compare coordinates, départements and regions their name, countries their code
 */
export const findDuplicateLocation = (
  locations: FormattedLocation[],
  candidate: FormattedLocation,
  locationType: LocationTypesEnum
): number =>
  locations.findIndex((location) => {
    switch (locationType) {
      case LocationTypesEnum.Cities:
        return isSameCity(location, candidate)
      case LocationTypesEnum.Countries:
        return Boolean(candidate.code) && location.code?.toLowerCase() === candidate.code?.toLowerCase()
      default:
        return (
          Boolean(location.name && candidate.name) &&
          normalizeBoundaryName(location.name as string) === normalizeBoundaryName(candidate.name as string)
        )
    }
  })

export const useAutoComplete = (
  locationType: LocationTypesEnum,
  name: string,
  countryCode: string,
  placesServiceAnchorId = 'autoCompleteGoogleMap',
  { onAdded, onDuplicate }: UseAutoCompleteCallbacks = {}
): useAutoCompleteInterface => {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<PlaceType[]>([])
  const loaded = useRef(false)
  const placesService = useRef<unknown>(null)
  const [field, , helpers] = useField({ name })
  const { setValue } = helpers
  const { value } = field

  const isLocationTypeCountries = useMemo((): boolean => locationType === LocationTypesEnum.Countries, [locationType])

  useEffect(() => {
    if (typeof window !== 'undefined' && !loaded.current) {
      loadPlacesLibrary().then(() => {
        loaded.current = true
      })
    }
  }, [])

  const fetchAutoCompleteOptions = useMemo(
    () =>
      throttle((request: Request, callback: (results?: PlaceType[]) => void) => {
        ;(autocompleteService.current as any).getPlacePredictions(request, callback)
      }, 200),
    []
  )

  useEffect(() => {
    let active = true

    if (!autocompleteService?.current && (window as any)?.google?.maps?.places?.AutocompleteService)
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService()

    if (!autocompleteService.current) return undefined

    const placesAnchor = document.getElementById(placesServiceAnchorId)
    if (!placesService.current && placesAnchor && (window as any)?.google?.maps?.places?.PlacesService)
      placesService.current = new (window as any).google.maps.places.PlacesService(placesAnchor)

    if (!placesService.current) return undefined

    if (inputValue === '') {
      setOptions([])
      return undefined
    }

    const filterMap: Record<LocationTypesEnum, string[]> = {
      [LocationTypesEnum.Cities]: ['locality', 'postal_code'],
      [LocationTypesEnum.Departements]: ['administrative_area_level_2'],
      [LocationTypesEnum.Regions]: ['administrative_area_level_1'],
      [LocationTypesEnum.Countries]: ['country'],
    }

    const typesFilter = filterMap[locationType] ?? []

    fetchAutoCompleteOptions(
      {
        input: inputValue,
        componentRestrictions: { country: isLocationTypeCountries ? '' : countryCode },
        types: typesFilter,
      },
      (results?: PlaceType[]) => {
        if (active && results) {
          const filteredOptions = results.filter((result) =>
            result.types.some((type: string) => typesFilter.includes(type))
          )
          setOptions(filteredOptions)
        }
      }
    )

    return () => {
      active = false
    }
  }, [inputValue, fetchAutoCompleteOptions, placesServiceAnchorId, locationType, countryCode, isLocationTypeCountries])

  const fetchLocationDetails = useMemo(
    () =>
      (value: PlaceType, callback: (placeDetails: PlaceDetails) => void): void => {
        ;(placesService.current as { getDetails: (...args: unknown[]) => void }).getDetails(
          {
            placeId: value.place_id,
            fields: ['geometry', 'name', 'address_components'],
          },
          callback
        )
      },
    []
  )

  const addNewLocationToSelection = (placeDetails: PlaceDetails): void => {
    let formattedLocation: FormattedLocation = {}
    switch (locationType) {
      case LocationTypesEnum.Cities:
        formattedLocation = {
          name: placeDetails.name,
          location: {
            latitude: placeDetails.geometry.location.lat(),
            longitude: placeDetails.geometry.location.lng(),
          },
          radius: 5,
        }
        break
      case LocationTypesEnum.Regions:
      case LocationTypesEnum.Departements:
        const coordinates = placeDetails.geometry.viewport.toJSON()
        formattedLocation = {
          name: placeDetails.name,
          geometry: {
            southwest: {
              latitude: coordinates.south,
              longitude: coordinates.west,
            },
            northeast: {
              latitude: coordinates.north,
              longitude: coordinates.east,
            },
          },
        }
        break
      case LocationTypesEnum.Countries:
        formattedLocation = {
          code: placeDetails.address_components[0].short_name.toLowerCase(),
          label: placeDetails.address_components[0].long_name,
        }
        break
    }
    const duplicateIndex = findDuplicateLocation(value as FormattedLocation[], formattedLocation, locationType)
    if (duplicateIndex >= 0) {
      onDuplicate?.(duplicateIndex)
      return
    }
    setValue([...value, formattedLocation])
    onAdded?.(formattedLocation)
  }

  const onChange = (newValue: Array<PlaceType | TargetingCircle>): void => {
    if (newValue.length < value.length) setValue(newValue)
    else fetchLocationDetails(last(newValue) as PlaceType, addNewLocationToSelection)
  }

  return { setInputValue, options, onChange }
}
